import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, TrendingUp, Users, Zap, Shield, Clock, Globe } from 'lucide-react';

const DigitalTransformation = () => {
  const { language } = useLanguage();

  const benefits = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Revenue Growth",
      description: "Average 25-40% revenue increase within 12 months"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Time Efficiency",
      description: "Reduce operational time by up to 60% with automation"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Enhanced Customer Experience",
      description: "360° customer journey optimization and personalization"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Risk Mitigation",
      description: "Advanced security protocols and compliance frameworks"
    }
  ];

  const industries = [
    { name: "Telecom", growth: "35%" },
    { name: "Insurance", growth: "42%" },
    { name: "Logistics", growth: "38%" },
    { name: "Energy", growth: "29%" },
    { name: "Finance", growth: "45%" },
    { name: "Healthcare", growth: "33%" }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Digital Maturity Assessment",
      description: "Comprehensive audit of your current digital infrastructure and readiness"
    },
    {
      step: "02", 
      title: "Strategic Roadmap Development",
      description: "Customized transformation plan aligned with your business objectives"
    },
    {
      step: "03",
      title: "Technology Implementation",
      description: "Deploy cutting-edge AI solutions and automation frameworks"
    },
    {
      step: "04",
      title: "Change Management & Training",
      description: "Ensure seamless adoption with comprehensive training programs"
    },
    {
      step: "05",
      title: "Continuous Optimization",
      description: "Ongoing monitoring, analytics, and performance improvements"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Zap className="w-4 h-4 mr-2" />
              Digital Transformation Leaders
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Digital Transformation Consulting That Delivers Results
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-4xl mx-auto mb-8">
              Transform your business with AI-powered solutions. From strategy to implementation, 
              we deliver measurable results for SMEs to enterprises across Africa and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90" asChild>
                <a href="/assessment">
                  Get Free Digital Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="/blog">
                  View Case Studies
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose QuantumBox AI for Digital Transformation?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We deliver fast, affordable, and effective transformation solutions that drive real business outcomes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
                    {benefit.icon}
                  </div>
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{benefit.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Results */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Proven Results Across Industries
            </h2>
            <p className="text-xl text-muted-foreground">
              Average growth rates achieved by our clients within 18 months
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="text-center p-6 bg-card rounded-lg border">
                <div className="text-3xl font-bold text-primary mb-2">{industry.growth}</div>
                <div className="text-sm text-muted-foreground">{industry.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Proven 5-Step Transformation Process
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From assessment to optimization, we guide you through every stage of your digital journey.
            </p>
          </div>
          
          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row items-start gap-6 p-6 border rounded-lg hover:shadow-md transition-shadow">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 500+ companies that have accelerated their growth with our digital transformation solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary">
              Schedule Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              Download Transformation Guide
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalTransformation;